import React from 'react';
import ReactDOM from 'react-dom';
import Styled from 'styled';
import defaultStyles, { ModalStyles } from './modal.scss';
import Modernizr from 'modernizr';
import { Mobilesafari } from 'modernizr/test/mobilesafari';

if (process.env.ENV_WEB) {
    require('modernizr/test/mobilesafari'); //tslint:disable-line
}

export interface Props {
    forwardedRef?: (ref: HTMLDivElement | null) => void;
    open?: boolean;
    willClose?: boolean;
    styles: ModalStyles;
    children?: React.ReactNode;
}

interface State {
    node: HTMLDivElement | null;
}

class Modal extends React.PureComponent<Props, State> {
    state: State = {
        node: null,
    };

    scrollTop?: number | null;
    scrollLeft?: number | null;

    tabIndexMap = new Map<Element, string | null>();
    addedBodyClasses = new Set<string>();

    addBodyClass = (className: string) => {
        if (!document.body.classList.contains(className)) {
            this.addedBodyClasses.add(className);
            document.body.classList.add(className);
        }
    };

    removeBodyClass = (className: string) => {
        if (this.addedBodyClasses.has(className)) {
            this.addedBodyClasses.delete(className);
            document.body.classList.remove(className);
        }
    };

    open() {
        const node = document.createElement('div');
        node.setAttribute('role', 'dialog');
        node.classList.add(...this.props.styles!.modal.split(' '));

        document.body.appendChild(node);
        this.props.styles.noScroll.split(' ').forEach(this.addBodyClass);

        if ((Modernizr as Mobilesafari).mobilesafari) {
            const list = document.querySelectorAll('form');

            if (list.length) {
                list.forEach(form => {
                    form.querySelectorAll('*').forEach(element => {
                        this.tabIndexMap.set(element, element.getAttribute('tabindex'));
                        element.setAttribute('tabindex', '-1');
                    });
                });
            }
        }

        return node;
    }

    handleSafari(node: HTMLDivElement) {
        if ((Modernizr as Mobilesafari).mobilesafari) {
            const list = node.querySelectorAll('form');

            if (list.length) {
                const scrollingElement = document.scrollingElement || document.documentElement;
                const { scrollTop, scrollLeft } = scrollingElement;

                this.scrollTop = scrollTop;
                this.scrollLeft = scrollLeft;
                this.props.styles.mobilesafari.split(' ').forEach(this.addBodyClass);
            }
        }
    }

    willClose() {
        this.props.styles!.noScroll.split(' ').forEach(this.removeBodyClass);

        if ((Modernizr as Mobilesafari).mobilesafari) {
            this.props.styles.mobilesafari.split(' ').forEach(this.removeBodyClass);

            this.tabIndexMap.forEach((tabindex, element) => {
                if (tabindex) {
                    element.setAttribute('tabindex', tabindex);
                } else {
                    element.removeAttribute('tabindex');
                }
            });

            this.tabIndexMap.clear();

            if (this.scrollTop) {
                (document.scrollingElement || document.documentElement).scrollTop = this.scrollTop;
                this.scrollTop = null;
            }

            if (this.scrollLeft) {
                (document.scrollingElement || document.documentElement).scrollLeft = this.scrollLeft;
                this.scrollLeft = null;
            }
        }
    }

    close() {
        const { node } = this.state;

        this.willClose();

        if (node && node.parentNode) {
            node.parentNode.removeChild(node);
        }
    }

    forwardRef = () => {
        if (this.props.forwardedRef) {
            this.props.forwardedRef(this.state.node);
        }
    };

    componentDidMount() {
        if (this.props.open) {
            this.setState({
                node: this.open(),
            });
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.willClose !== prevProps.willClose && this.props.willClose) {
            this.willClose();
        }

        if (this.props.open !== prevProps.open) {
            if (this.props.open) {
                this.setState({
                    node: this.open(),
                });
            } else {
                this.close();

                this.setState({
                    node: null,
                });
            }
        }

        if (this.state.node !== prevState.node) {
            this.forwardRef();

            if (this.state.node) {
                this.handleSafari(this.state.node);
            }
        }
    }

    componentWillUnmount() {
        this.close();
    }

    render() {
        const { node } = this.state;

        if (!node) {
            return null;
        }

        const { children } = this.props;

        return ReactDOM.createPortal(children, node);
    }
}

export default Styled(defaultStyles)(Modal);
